import type { AiManagedGroupDiagnostics, AiManagedGroupPolicy } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { ManagedGroupDiagnosticsPanel } from "@/components/managed-group/ManagedGroupDiagnosticsPanel";
import { cn } from "@/lib/utils";

interface ReviewStepProps {
  diagnostics?: AiManagedGroupDiagnostics;
  diagnosticsError: boolean;
  diagnosticsLoading: boolean;
  name: string;
  policy: AiManagedGroupPolicy;
  selectedCount: number;
  shopName: string;
  workspaceAiEnabled: boolean;
  onRetryDiagnostics: () => void;
}

export function ReviewStep({
  diagnostics,
  diagnosticsError,
  diagnosticsLoading,
  name,
  policy,
  selectedCount,
  shopName,
  workspaceAiEnabled,
  onRetryDiagnostics,
}: ReviewStepProps): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  return (
    <div className="flex h-full flex-col gap-[14px] overflow-y-auto px-7 py-[18px] paper-scrollbar">
      <section className="flex flex-col gap-2">
        <h3 className="text-[11px] leading-[14px] font-semibold">{t("create.groupDetails")}</h3>
        <dl data-slot="review-group-details" className="grid grid-cols-3 gap-2">
          <ReviewCard label={t("create.name")} value={name} />
          <ReviewCard label={t("create.shop")} value={shopName} />
          <ReviewCard
            label={t("create.campaigns")}
            value={t("create.selected", { count: selectedCount })}
            tabular
          />
        </dl>
      </section>

      <section className="flex flex-col gap-2 border-t border-line pt-[13px]">
        <h3 className="text-[11px] leading-[14px] font-semibold">{t("create.sharedPolicy")}</h3>
        <dl data-slot="review-shared-policy" className="grid grid-cols-2 gap-[18px]">
          <div className="flex min-w-0 flex-col gap-[5px]">
            <ReviewItem
              label={t("policy.businessGoal")}
              value={t(`policy.goals.${policy.businessGoal}`)}
            />
            <ReviewItem
              label={t("policy.targetAcos")}
              value={`${Math.round(policy.targetAcos * 100)}%`}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-[5px]">
            <ReviewItem
              label={t("policy.riskPreference")}
              value={t(`policy.risks.${policy.riskPreference}`)}
            />
            <ReviewItem
              label={t("policy.executionMode")}
              value={t(`policy.modes.${policy.executionMode}`)}
            />
          </div>
        </dl>
      </section>

      <section className="flex flex-col gap-[9px] border-t border-line pt-[13px]">
        <ManagedGroupDiagnosticsPanel
          compact
          diagnostics={diagnostics}
          isLoading={diagnosticsLoading}
          isError={diagnosticsError}
          onRetry={onRetryDiagnostics}
        />
        <div
          data-slot="workspace-ai-review-notice"
          role={workspaceAiEnabled ? "status" : "alert"}
          className={cn(
            "flex min-h-[38px] items-center gap-[7px] rounded-[8px] px-[11px] py-[9px] text-[9px] leading-3 text-muted-foreground",
            workspaceAiEnabled
              ? "bg-subtle"
              : "border border-badge-warning-border bg-badge-warning-background text-badge-warning-foreground",
          )}
        >
          <span
            className={cn(
              "size-1.5 shrink-0 rounded-full",
              workspaceAiEnabled ? "bg-success" : "bg-warning",
            )}
          />
          <p>
            {workspaceAiEnabled
              ? t("create.workspaceAiActiveReviewHint")
              : t("create.workspaceAiOffReviewHint")}
          </p>
        </div>
      </section>
    </div>
  );
}

function ReviewItem({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="flex items-start justify-between gap-3 text-[10px] leading-[14px]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="max-w-[60%] text-right font-medium text-foreground">{value}</dd>
    </div>
  );
}

function ReviewCard({
  label,
  tabular = false,
  value,
}: {
  label: string;
  tabular?: boolean;
  value: string;
}): React.ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-[8px] bg-subtle px-[11px] py-[9px]">
      <dt className="text-[8px] leading-[10px] text-muted-foreground uppercase">{label}</dt>
      <dd
        className={cn(
          "truncate text-[10px] leading-[14px] font-semibold",
          tabular && "font-mono tabular-nums",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
